import { createContext, type PropsWithChildren, useContext } from "react";

interface TranslateFunction<T extends Record<string, string>> {
  (key: keyof T): string;
}

type SimpleTranslateProviderProps<T extends Record<string, string>> =
  PropsWithChildren<{
    translations: T;
  }>;

export type Translate<T extends Record<string, string>> = ReturnType<
  typeof getDefaultTranslateFunction<T>
>;

type DefineTranslation<T extends Record<string, string>> =
  T extends Record<string, string> ? keyof T : string;

const getTranslateFunction = ({
  translations,
}: {
  translations: Record<string, string>;
}) => {
  return <T extends Record<string, string>>() => {
    return (key: DefineTranslation<T>) => translations[key] || key;
  };
};

const getDefaultTranslateFunction = getTranslateFunction({
  translations: {},
});

/**
 * This is the main context provider for translations in this repo. It provides the `t` function as a property on an object.
 */
export const SimpleTranslateContext = createContext({
  t: getDefaultTranslateFunction(),
});

/**
 * We provide type enhancements via an optional generic where we will raise a type
 * error if the path argument is not present as a key in the specified locale type.
 *
 * @example
 *
 * // en_US.json
 * { "path.to.key": "Hello!"}
 *
 * // Component.tsx
 * import locale from './en_US.json';
 *
 * type LocaleType = typeof locale // type LocaleType = {"path.to.key": string}
 *
 * const Component = () => {
 *   const translate = useSimpleTranslate<LocaleType>();
 *   const translateWithoutGeneric = useSimpleTranslate();
 *
 *   const translation = translate("path.to.key") // string
 *   const translationWithTypeError = translate("not.a.path.to.key") // TypeScript error: Argument of type '""not.a.path.to.key"' is not assignable to parameter of type '"path.to.key"'
 *   const translationWithoutGenericOrTypeError = translateWithoutGeneric("path.to.key") // string
 *   const translationWithoutGenericOrTypeError = translateWithoutGeneric("not.a.path.to.key") // string, but the UI will render just the string "not.a.path.to.key" instead of Hello!
 * }
 */
export function useSimpleTranslate<
  T extends Record<string, string>,
>(): Translate<T> {
  const context = useContext(SimpleTranslateContext);
  return context.t;
}

/**
 * Registers the provided translations in the context so
 * children can use the translate function
 *
 * Usage Examples:
 *
 * Modern Functional Component:
 * ```tsx
 * import { useSimpleTranslate } from './SimpleTranslateProvider';
 *
 * // Define your translation type
 * type TranslationKeys = {
 *   'welcome.title': string;
 *   'welcome.message': string;
 *   'button.submit': string;
 * };
 *
 * // Define your translations
 * const translations: TranslationKeys = {
 *   'welcome.title': 'Welcome!',
 *   'welcome.message': 'Hello, world!',
 *   'button.submit': 'Submit',
 * };
 *
 * function MyComponent() {
 *   const t = useSimpleTranslate<TranslationKeys>();
 *   return <div>{t('welcome.message')}</div>; // TypeScript will validate the key
 * }
 *
 * // Provider setup
 * function App() {
 *   return (
 *     <SimpleTranslateProvider translations={translations}>
 *       <MyComponent />
 *     </SimpleTranslateProvider>
 *   );
 * }
 * ```
 *
 * Legacy Class Component:
 * ```tsx
 * import { SimpleTranslateContext } from './SimpleTranslateProvider';
 *
 * class MyComponent extends React.Component {
 *   static contextType = SimpleTranslateContext;
 *
 *   const { t: translate } = this.context;
 *   render() {
 *     return <div>{translate("welcome.message")}</div>;
 *   }
 * }
 * ```
 */
const SimpleTranslateProvider = <T extends Record<string, string>>({
  children,
  translations,
}: SimpleTranslateProviderProps<T>) => {
  // Create translate function that does translations[key]
  const translate: TranslateFunction<T> = (key: keyof T): string => {
    return translations[key] || String(key);
  };

  const contextValue = {
    t: translate,
  };

  return (
    <SimpleTranslateContext.Provider value={contextValue}>
      {children}
    </SimpleTranslateContext.Provider>
  );
};

export default SimpleTranslateProvider;
